/**
 * Firebase Configuration
 *
 * Initializes Firebase app with web config from environment variables.
 * Used for Firebase Auth when Python backend is enabled.
 */

import type {FirebaseApp, FirebaseOptions} from 'firebase/app';
import {getApps, initializeApp} from 'firebase/app';
import type {Auth} from 'firebase/auth';
import {getAuth} from 'firebase/auth';
import {logger} from '@/shared/lib/logger/logger';

/**
 * Get Firebase configuration from environment variables
 */
function getFirebaseConfig(): FirebaseOptions {
  const apiKey = import.meta.env['VITE_FIREBASE_API_KEY'];
  const authDomain = import.meta.env['VITE_FIREBASE_AUTH_DOMAIN'];
  const projectId = import.meta.env['VITE_FIREBASE_PROJECT_ID'];
  const appId = import.meta.env['VITE_FIREBASE_APP_ID'];

  if (!apiKey || !authDomain || !projectId || !appId) {
    throw new Error(
      'Missing Firebase configuration. ' +
      'Required env vars: VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, ' +
      'VITE_FIREBASE_PROJECT_ID, VITE_FIREBASE_APP_ID'
    );
  }

  return {
    apiKey,
    authDomain,
    projectId,
    appId,
  };
}

let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;

/**
 * Initialize Firebase app
 *
 * Returns existing app if already initialized, otherwise creates new app.
 *
 * @returns Firebase app instance
 */
function initializeFirebase(): FirebaseApp {
  if (firebaseApp) {
    return firebaseApp;
  }

  const apps = getApps();
  if (apps.length > 0) {
    firebaseApp = apps[0] || null;
    if (!firebaseApp) {
      throw new Error('Firebase app initialization failed');
    }
    return firebaseApp;
  }

  try {
    const config = getFirebaseConfig();
    firebaseApp = initializeApp(config);
    return firebaseApp;
  } catch (error) {
    logger.error('[Firebase Config] Failed to initialize Firebase', error instanceof Error ? error : new Error(String(error)), {
      component: 'firebase.config',
      action: 'initialize_firebase',
    });
    throw error;
  }
}

/**
 * Get Firebase Auth instance
 *
 * Initializes Firebase app if not already initialized.
 *
 * @returns Firebase Auth instance
 */
export function getFirebaseAuth(): Auth {
  if (firebaseAuth) {
    return firebaseAuth;
  }

  const app = initializeFirebase();
  firebaseAuth = getAuth(app);
  return firebaseAuth;
}

/**
 * Check if Firebase is configured
 */
export function isFirebaseConfigured(): boolean {
  try {
    const apiKey = import.meta.env['VITE_FIREBASE_API_KEY'];
    const authDomain = import.meta.env['VITE_FIREBASE_AUTH_DOMAIN'];
    const projectId = import.meta.env['VITE_FIREBASE_PROJECT_ID'];
    const appId = import.meta.env['VITE_FIREBASE_APP_ID'];

    return !!(apiKey && authDomain && projectId && appId);
  } catch {
    return false;
  }
}
